import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowBack,
  Login as LoginIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import RecentFilesView from './RecentFilesView';
import { SessionManager } from './SessionManager';
import UploadTabs from './UploadTabs';
import { useAuth } from './contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface LeftNavigationProps {
  onFileSelect: (fileId: number) => void;
  onDeleteAllFiles?: () => void;
  auditioBackendManager?: any;
  refreshTrigger?: number;
  isOpen?: boolean;
  onToggle?: (open: boolean) => void;
  showToggleButton?: boolean;
  recentFilesCount?: number;
  onWidthChange?: (width: number) => void;
  showLogs: boolean;
  setShowLogs: (show: boolean) => void;
}

// Separate component for the toggle button that can be used in the app bar
export const LeftNavToggleButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    data-testid="left-nav-toggle"
    sx={{
      color: 'white',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      }
    }}
  >
    <MenuIcon />
  </IconButton>
);

const LeftNavigation: React.FC<LeftNavigationProps> = ({
  onFileSelect,
  onDeleteAllFiles,
  auditioBackendManager,
  refreshTrigger,
  isOpen = false,
  onToggle,
  showToggleButton = true,
  recentFilesCount = 0,
  showLogs,
  setShowLogs,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Debug logging
  useEffect(() => {
    console.log('🔧 LeftNavigation - isOpen changed:', isOpen);
  }, [isOpen]);

  // Handle clicks outside the drawer on desktop
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !isMobile) {
        // Check if click is outside the drawer area
        const drawerElement = document.querySelector('[data-testid="left-nav-drawer"]');
        const toggleButton = document.querySelector('[data-testid="left-nav-toggle"]');
        
        if (drawerElement && !drawerElement.contains(event.target as Node) && 
            toggleButton && !toggleButton.contains(event.target as Node)) {
          handleToggle(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

  const handleToggle = (open: boolean) => {
    console.log('🔧 LeftNavigation - handleToggle called with:', open);
    if (onToggle) {
      onToggle(open);
    }
  };

  // Handle clicks outside the drawer
  const handleBackdropClick = (event: React.MouseEvent) => {
    // Only close if clicking on the backdrop (not on the drawer content)
    if (event.target === event.currentTarget) {
      handleToggle(false);
    }
  };

  const drawerWidth = isMobile ? '100%' : 280;

  const drawerContent = (
    <Box
      className="left-nav"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'static !important',
        transform: 'none !important',
        zIndex: 'auto !important',
      }}
    >
      {/* Header */}
      <Box className="nav-header">
        <Box className="nav-logo">
          <Box className="nav-logo-icon">
            <svg viewBox="0 0 24 24">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
            </svg>
          </Box>
          <span>Recent Tracks</span>
          <Box className="nav-section-count">{recentFilesCount}</Box>
        </Box>
        <IconButton className="nav-toggle" onClick={() => handleToggle(false)}>
          <ArrowBack />
        </IconButton>
      </Box>
      
      {/* Action Buttons - Only show when authenticated */}
      {user ? (
        <>
          <Box className="nav-actions">
            <button
              className="nav-btn nav-btn-primary"
              onClick={() => setUploadDialogOpen(true)}
            >
              <Box className="nav-btn-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
                </svg>
              </Box>
              <span>Add New Track</span>
            </button>
            
            <button
              className="nav-btn nav-btn-secondary"
              onClick={onDeleteAllFiles}
            >
              <Box className="nav-btn-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                </svg>
              </Box>
              <span>Delete All Uploaded Files</span>
            </button>
          </Box>
          
          {/* Recent Files Section - will flex to fill available space */}
          <Box className="nav-section" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box className="recent-files" sx={{ height: '100%', overflow: 'auto' }}>
              <RecentFilesView
                onFileSelect={(fileId) => {
                  onFileSelect(fileId);
                  // Close drawer on mobile after file selection
                  if (isMobile) {
                    handleToggle(false);
                  }
                }}
                auditioBackendManager={auditioBackendManager}
                refreshTrigger={refreshTrigger}
                isSidebar={true}
              />
            </Box>
          </Box>
        </>
      ) : (
        /* Login Prompt - Show when not authenticated */
        <Box className="nav-section" sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3 }}>
          <LockIcon sx={{ width: 48, height: 48, color: 'var(--primary)', mb: 2, opacity: 0.7 }} />
          <Typography variant="body1" sx={{ 
            color: 'var(--text-color)', 
            mb: 2, 
            textAlign: 'center',
            opacity: 0.8,
            fontSize: '0.95rem',
            lineHeight: 1.6
          }}>
            Please log in to upload tracks and manage your files.
          </Typography>
          <button
            className="nav-btn nav-btn-primary"
            onClick={() => {
              navigate('/login');
              handleToggle(false);
            }}
            style={{ width: '100%', marginTop: '8px' }}
          >
            <Box className="nav-btn-icon">
              <LoginIcon />
            </Box>
            <span>Log In</span>
          </button>
        </Box>
      )}
      
      {/* Settings Section - anchored at bottom */}
      <Box className="nav-section" sx={{ 
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        paddingTop: 2,
        paddingBottom: 2,
        marginTop: 'auto', // Push to bottom
        flexShrink: 0 // Don't shrink
      }}>
        <Typography variant="subtitle2" className="settings-title">
          <Box className="settings-icon">
            <svg viewBox="0 0 24 24">
              <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.03L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11.03C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
            </svg>
          </Box>
          Settings
        </Typography>
        <button
          className={`nav-btn ${showLogs ? 'nav-btn-primary' : 'nav-btn-secondary'}`}
          onClick={() => setShowLogs(!showLogs)}
        >
          <Box className="nav-btn-icon">
            <svg viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </Box>
          <span>{showLogs ? 'Hide Logs' : 'Show Logs'}</span>
        </button>
      </Box>
    </Box>
  );

  console.log('🔧 LeftNavigation - Rendering with isOpen:', isOpen, 'isMobile:', isMobile);

  return (
    <>
      {/* Backdrop overlay for outside clicks (only on mobile) */}
      {isOpen && isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1199,
          }}
          onClick={() => handleToggle(false)}
        />
      )}

      {/* Drawer */}
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={() => handleToggle(false)}
        variant={isMobile ? "temporary" : "persistent"}
        data-testid="left-nav-drawer"
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#fff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      
      {/* File Upload Dialog */}
      {auditioBackendManager && (
        <UploadTabs
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          auditioBackendManager={auditioBackendManager}
          audioFile=""
          setAudioFile={() => {}}
          audioFileId={0}
          setAudioFileId={() => {}}
          renderTrigger={0}
          setRenderTrigger={() => {}}
          isDialog={true}
        />
      )}
    </>
  );
};

export default LeftNavigation; 